import { blueprints, experimentalEffects, modules as allModules, ships } from "./data.ts";
import edsyRulesFile from "../data/edsy-outfitting.json";
import { edsyShipFor } from "./edsy-data.ts";
import type { ModuleDef, Ship, ShipBuildSlot, ShipBulkhead } from "./types.ts";

export interface SlotFitResult {
  ok: boolean;
  reason: string | null;
}

export interface BuildFit {
  slot: ShipBuildSlot;
  module: ModuleDef | null;
  bulkhead: ShipBulkhead | null;
  experimentalKey?: string | null;
  engineering?: {
    blueprintFdName: string | null;
    blueprintName: string | null;
    grade: number | null;
  };
}

export interface BuildStats {
  moduleCount: number;
  price: number;
  unladenMass: number;
  ladenMass: number;
  currentMass: number;
  maxMass: number;
  cargoCapacity: number;
  cargoLoad: number;
  fuelCapacity: number;
  fuelLoad: number;
  fuelReserve: number;
  jumpMax: number | null;
  jumpUnladen: number | null;
  jumpLaden: number | null;
  rangeUnladen: number | null;
  rangeLaden: number | null;
  speed: number | null;
  boost: number | null;
  shield: number | null;
  armour: number | null;
  armourKineticResistance: number | null;
  armourThermalResistance: number | null;
  armourExplosiveResistance: number | null;
  armourCausticResistance: number | null;
  powerCapacity: number | null;
  powerDraw: number | null;
  powerRetracted: number | null;
  warnings: string[];
}

const CORE_GROUP_LABELS: Record<string, string> = {
  pp: "Power Plant",
  t: "Thrusters",
  fsd: "Frame Shift Drive",
  ls: "Life Support",
  pd: "Power Distributor",
  s: "Sensors",
  ft: "Fuel Tank",
};

interface EdsyModuleRule { type: string; ships: string[] | null; blueprints: string[]; experimentals: string[]; limit: string | null; noUndersize?: boolean; hidden?: boolean; id?: number | null }
const edsyRules = edsyRulesFile.modules as Record<string, EdsyModuleRule>;
export function edsyRuleForModule(module: ModuleDef): EdsyModuleRule | undefined {
  return edsyRules[module.symbol.toLowerCase().replace(/__pre_.+$/, "")];
}

export function blueprintsForModule(module: ModuleDef) {
  if (module.preEngineered?.reengineerable === false) return [];
  const rule = edsyRuleForModule(module);
  const group = /_armour_/i.test(module.symbol) ? "bh" : module.group;
  const matches = blueprints.filter(blueprint => rule
    ? Boolean(blueprint.fdName && rule.blueprints.includes(blueprint.fdName))
    : Boolean(group && blueprint.moduleKeys.includes(group)));
  const byName = new Map<string, (typeof blueprints)[number]>();
  for (const blueprint of matches) {
    const current = byName.get(blueprint.name);
    if (!current || blueprint.moduleKeys.includes(group ?? "")) byName.set(blueprint.name, blueprint);
  }
  return [...byName.values()].sort((a, b) => a.name.localeCompare(b.name));
}

export function experimentalEffectsForModule(module: ModuleDef) {
  if (module.preEngineered?.canApplyExperimental === false) return [];
  const rule = edsyRuleForModule(module);
  return rule ? experimentalEffects.filter(effect => rule.experimentals.includes(effect.key)).sort((a, b) => a.name.localeCompare(b.name)) : [];
}

function normalizeShipIdentifier(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, "");
}

const shipNamesByIdentifier = new Map<string, string>();
function matchesShipIdentifier(ship: Ship, identifier: string): boolean {
  const allowedIdentifiers = [ship.slug, ship.name, ship.fdSymbol, edsyShipFor(ship)?.fdname]
    .filter((entry): entry is string => Boolean(entry))
    .map(normalizeShipIdentifier);
  return allowedIdentifiers.includes(normalizeShipIdentifier(identifier));
}

for (const knownShip of ships) {
  for (const identifier of [knownShip.slug, knownShip.name, knownShip.fdSymbol]) {
    if (identifier) shipNamesByIdentifier.set(normalizeShipIdentifier(identifier), knownShip.name);
  }
}

function stat(module: ModuleDef | null, key: string): number | null {
  const value = module?.stats[key];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function hasEligibleGroup(slot: ShipBuildSlot, module: ModuleDef): boolean {
  if (!slot.eligible) return true;
  return module.group !== null && slot.eligible.includes(module.group);
}

export function moduleFitsSlot(
  ship: Ship,
  slot: ShipBuildSlot,
  module: ModuleDef,
): SlotFitResult {
  if (module.category === "mercgear") {
    return { ok: false, reason: "On-foot equipment cannot be fitted to a ship." };
  }

  if (module.ship) {
    if (!matchesShipIdentifier(ship, module.ship)) {
      const requiredShip = shipNamesByIdentifier.get(normalizeShipIdentifier(module.ship)) ?? module.ship;
      return { ok: false, reason: `This module is restricted to the ${requiredShip}.` };
    }
  }
  const sourceRule = edsyRuleForModule(module);
  if (sourceRule?.ships && !sourceRule.ships.some(identifier => matchesShipIdentifier(ship, identifier))) {
    return { ok: false, reason: "This hull is not permitted by the module's EDSY hull restrictions." };
  }
  if (sourceRule?.noUndersize && module.class !== slot.size) return { ok: false, reason: "This module cannot be fitted below the slot's class." };
  const sourceShip = edsyShipFor(ship);
  if (sourceRule && sourceShip && slot.sourceGroup !== undefined && slot.sourceIndex !== undefined) {
    const allowedTypes = sourceShip.reserved?.[slot.sourceGroup]?.[String(slot.sourceIndex)];
    if (allowedTypes && !allowedTypes[sourceRule.type]) return { ok: false, reason: "This dedicated slot does not accept this module type." };
    const name = (slot.fdSlotName ?? "").toUpperCase();
    if (sourceRule.type === "icr" && sourceRule.ships && !name.startsWith("CARGO")) return { ok: false, reason: "This Mk II rack requires the dedicated cargo slot." };
    if (sourceRule.type === "imlc" && sourceRule.ships && !name.startsWith("LIMPETCONTROLLER")) return { ok: false, reason: "This controller requires its dedicated limpet slot." };
    if (sourceRule.type === "ipvh" && !module.symbol.toUpperCase().includes("LARGEBUGGYBAY") && name.startsWith("FIGHTERBAY")) return { ok: false, reason: "This hangar slot requires an eligible large vehicle hangar." };
    if (sourceRule.id === 80339 && ship.fdSymbol?.toLowerCase() === "anacondamkii" && (slot.sourceIndex < 3 || slot.sourceIndex > 4)) return { ok: false, reason: "This repeater requires a lower large hardpoint." };
  }

  if (slot.kind === "bulkhead") {
    if (module.category !== "standard" || !/_armour_/i.test(module.symbol) || !module.ship || !matchesShipIdentifier(ship, module.ship)) {
      return { ok: false, reason: `Requires a bulkhead for the ${ship.name}.` };
    }
    return { ok: true, reason: null };
  }

  if (slot.kind === "core") {
    if (module.category !== "standard" || module.group !== slot.group) {
      return {
        ok: false,
        reason: `Requires a ${CORE_GROUP_LABELS[slot.group ?? ""] ?? "core"} module.`,
      };
    }
    if (slot.size !== null && (module.class === null || module.class > slot.size || (["ls", "s"].includes(slot.group ?? "") && module.class !== slot.size))) {
      return { ok: false, reason: `Requires class ${slot.size}${["ls", "s"].includes(slot.group ?? "") ? "" : " or smaller"}.` };
    }
    if (module.group === "sg" && stat(module, "maxmass") !== null) {
      const maxMass = stat(module, "maxmass") as number;
      if (maxMass < (ship.hullMass ?? 0)) {
        return { ok: false, reason: "Shield generator maximum mass is below the hull mass." };
      }
    }
    return { ok: true, reason: null };
  }

  if (slot.kind === "hardpoint") {
    if (module.category !== "hardpoint") {
      return { ok: false, reason: "Requires a hardpoint weapon." };
    }
    if (slot.size !== null && module.class !== null && module.class > slot.size) {
      return { ok: false, reason: `Requires class ${slot.size} or smaller.` };
    }
    if (!hasEligibleGroup(slot, module)) {
      return { ok: false, reason: "Not eligible for this hardpoint." };
    }
    return { ok: true, reason: null };
  }

  if (slot.kind === "utility") {
    if (module.category !== "utility") {
      return { ok: false, reason: "Requires a utility module." };
    }
    return { ok: true, reason: null };
  }

  if (module.category !== "internal" && !(module.category === "standard" && module.group === "ft")) {
    return { ok: false, reason: "Requires an optional internal module." };
  }
  if (slot.size !== null && module.class !== null && module.class > slot.size) {
    return { ok: false, reason: `Requires class ${slot.size} or smaller.` };
  }
  if (!hasEligibleGroup(slot, module)) {
    return { ok: false, reason: "Not eligible for this restricted slot." };
  }
  if (module.group === "sg" && stat(module, "maxmass") !== null) {
    const maxMass = stat(module, "maxmass") as number;
    if (maxMass < (ship.hullMass ?? 0)) {
      return { ok: false, reason: "Shield generator maximum mass is below the hull mass." };
    }
  }
  return { ok: true, reason: null };
}

export function modulesForSlot(ship: Ship, slot: ShipBuildSlot): ModuleDef[] {
  const selectable = allModules.filter((module) => !module.symbol.toLowerCase().endsWith("_free") && !edsyRuleForModule(module)?.hidden);
  return selectable
    .filter((module) => moduleFitsSlot(ship, slot, module).ok)
    .sort((a, b) => {
      const classOrder = (b.class ?? 0) - (a.class ?? 0);
      if (classOrder !== 0) return classOrder;
      return (a.rating ?? "").localeCompare(b.rating ?? "");
    });
}

export function massCurveMultiplier(
  mass: number,
  minMass: number,
  optMass: number,
  maxMass: number,
  minMultiplier: number,
  optMultiplier: number,
  maxMultiplier: number,
): number {
  if (maxMass <= minMass) return optMultiplier;
  const range = maxMass - minMass;
  const ratio = Math.max(0, Math.min(1, (maxMass - mass) / range));
  const numerator = Math.max(0.000001, optMultiplier - minMultiplier);
  const denominator = Math.max(0.000001, maxMultiplier - minMultiplier);
  const optRatio = optMass === minMass ? 1 : Math.max(0.000001, (maxMass - optMass) / range);
  const exponent = Math.log(numerator / denominator) / Math.log(optRatio);
  return minMultiplier + Math.pow(ratio, exponent) * (maxMultiplier - minMultiplier);
}

export function jumpDistance(
  mass: number,
  fuel: number,
  maxFuelPerJump: number,
  optimalMass: number,
  fuelMultiplier: number,
  fuelPower: number,
): number | null {
  if (mass <= 0 || fuel <= 0 || maxFuelPerJump <= 0 || optimalMass <= 0) return null;
  if (fuelMultiplier <= 0 || fuelPower <= 0) return null;
  const used = Math.min(fuel, maxFuelPerJump);
  return Math.pow(used / fuelMultiplier, 1 / fuelPower) * optimalMass / (mass + used);
}

export function jumpRange(
  mass: number,
  fuelCapacity: number,
  maxFuelPerJump: number,
  optimalMass: number,
  fuelMultiplier: number,
  fuelPower: number,
): number | null {
  let remaining = fuelCapacity;
  let range = 0;
  for (let jumps = 0; remaining >= 0.000001 && jumps < 10000; jumps += 1) {
    const distance = jumpDistance(
      mass,
      remaining,
      maxFuelPerJump,
      optimalMass,
      fuelMultiplier,
      fuelPower,
    );
    if (distance === null) break;
    range += distance;
    remaining -= Math.min(remaining, maxFuelPerJump);
  }
  return range > 0 ? range : null;
}

function moduleMass(module: ModuleDef | null): number {
  return module?.mass ?? 0;
}

function modulePower(module: ModuleDef | null): number {
  return module?.power ?? 0;
}

export type EngineeringRoll = "minimum" | "maximum";

/** Applies published roll bounds to matching numeric module fields and reports missing fields. */
export function applyEngineeringModifiers(
  module: ModuleDef,
  engineering: NonNullable<BuildFit["engineering"]>,
  roll: EngineeringRoll = "maximum",
): { module: ModuleDef; incomplete: boolean } {
  if (module.preEngineered || (!engineering.blueprintFdName && !engineering.blueprintName)) {
    return { module, incomplete: false };
  }
  if (!Number.isInteger(engineering.grade) || engineering.grade === null) {
    return { module, incomplete: true };
  }

  const candidates = blueprints.filter((blueprint) =>
    module.group !== null && blueprint.moduleKeys.includes(module.group)
  );
  const fdName = engineering.blueprintFdName?.trim().toLowerCase();
  const name = engineering.blueprintName?.trim().toLowerCase();
  const byFdName = fdName
    ? candidates.filter((blueprint) => blueprint.fdName?.toLowerCase() === fdName)
    : [];
  const byName = name
    ? candidates.filter((blueprint) =>
      blueprint.name.toLowerCase() === name || blueprint.displayName.toLowerCase() === name
    )
    : [];
  const blueprint = byName.length === 1
    ? byName[0]
    : byFdName.length === 1
      ? byFdName[0]
      : undefined;
  const grade = blueprint?.grades.find((candidate) => candidate.grade === engineering.grade);
  if (!grade) return { module, incomplete: true };

  let mass = module.mass;
  let power = module.power;
  let integrity = module.integrity;
  const stats = { ...module.stats };
  let incomplete = false;

  for (const [property, rawValues] of Object.entries(grade.modifiers)) {
    const values = Array.isArray(rawValues)
      ? rawValues.filter((value): value is number => typeof value === "number" && Number.isFinite(value))
      : typeof rawValues === "number" && Number.isFinite(rawValues)
        ? [rawValues]
        : [];
    if (values.length === 0) {
      incomplete = true;
      continue;
    }

    const modifier = values[roll === "minimum" ? 0 : values.length - 1];
    if (modifier === undefined) {
      incomplete = true;
      continue;
    }
    const current = property === "mass"
      ? mass
      : property === "power"
        ? power
        : property === "integrity"
          ? integrity
          : stats[property];
    if (typeof current !== "number" || !Number.isFinite(current)) {
      incomplete = true;
      continue;
    }

    const adjusted = current * (1 + modifier);
    if (property === "mass") mass = adjusted;
    else if (property === "power") power = adjusted;
    else if (property === "integrity") integrity = adjusted;
    else stats[property] = adjusted;
  }

  return { module: { ...module, mass, power, integrity, stats }, incomplete };
}

export function computeBuildStats(
  ship: Ship,
  fits: BuildFit[],
  options: {
    cargo?: number;
    fuel?: number;
    cargoLoad?: number;
    fuelLoad?: number;
    engineeringRoll?: EngineeringRoll;
  } = {},
): BuildStats {
  const engineeringWarnings = new Set<string>();
  const engineeringRoll = options.engineeringRoll ?? "maximum";
  const modules = fits.map((fit) => {
    if (!fit.module || !fit.engineering) return fit.module;
    const result = applyEngineeringModifiers(fit.module, fit.engineering, engineeringRoll);
    if (result.incomplete) {
      engineeringWarnings.add("Some engineering modifiers could not be applied from the verified blueprint data.");
    }
    return result.module;
  }).filter((module): module is ModuleDef => Boolean(module));
  if (fits.some((fit) => fit.experimentalKey)) {
    engineeringWarnings.add(
      "Experimental-effect numerical modifiers are unavailable in the verified game data.",
    );
  }
  const bulkhead = fits.find((fit) => fit.slot.kind === "bulkhead")?.bulkhead ?? null;
  const fsd = modules.find((module) => module.group === "fsd") ?? null;
  const thrusters = modules.find((module) => module.group === "t") ?? null;
  const powerPlant = modules.find((module) => module.group === "pp") ?? null;
  const powerDistributor = modules.find((module) => module.group === "pd") ?? null;
  const shieldGenerator = modules.find((module) => ["sg", "bsg", "psg"].includes(module.group ?? "")) ?? null;

  const cargoCapacity =
    options.cargo ??
    modules.reduce((total, module) => total + (stat(module, "cargo") ?? 0), 0);
  const fuelCapacity =
    options.fuel ?? modules.reduce((total, module) => total + (stat(module, "fuel") ?? 0), 0);
  const cargoLoad = Math.min(Math.max(options.cargoLoad ?? cargoCapacity, 0), cargoCapacity);
  const fuelLoad = Math.min(Math.max(options.fuelLoad ?? fuelCapacity, 0), fuelCapacity);
  const unladenMass =
    (ship.hullMass ?? 0) +
    (bulkhead?.mass ?? 0) +
    modules.reduce((total, module) => total + moduleMass(module), 0);
  const ladenMass = unladenMass + cargoLoad;
  const maxFuelPerJump = stat(fsd, "maxfuel");
  const optimalMass = stat(fsd, "optmass");
  const fuelMultiplier = stat(fsd, "fuelmul");
  const fuelPower = stat(fsd, "fuelpower");

  const jumpMax =
    fsd && maxFuelPerJump !== null && optimalMass !== null && fuelMultiplier !== null && fuelPower !== null
      ? jumpDistance(
          unladenMass,
          Math.min(fuelLoad, maxFuelPerJump),
          maxFuelPerJump,
          optimalMass,
          fuelMultiplier,
          fuelPower,
        )
      : null;
  const jumpUnladen =
    fsd && maxFuelPerJump !== null && optimalMass !== null && fuelMultiplier !== null && fuelPower !== null
      ? jumpDistance(
          unladenMass,
          fuelLoad,
          maxFuelPerJump,
          optimalMass,
          fuelMultiplier,
          fuelPower,
        )
      : null;
  const jumpLaden =
    fsd && maxFuelPerJump !== null && optimalMass !== null && fuelMultiplier !== null && fuelPower !== null
      ? jumpDistance(
          ladenMass,
          fuelLoad,
          maxFuelPerJump,
          optimalMass,
          fuelMultiplier,
          fuelPower,
        )
      : null;
  const rangeUnladen =
    fsd && maxFuelPerJump !== null && optimalMass !== null && fuelMultiplier !== null && fuelPower !== null
      ? jumpRange(
          unladenMass,
          fuelLoad,
          maxFuelPerJump,
          optimalMass,
          fuelMultiplier,
          fuelPower,
        )
      : null;
  const rangeLaden =
    fsd && maxFuelPerJump !== null && optimalMass !== null && fuelMultiplier !== null && fuelPower !== null
      ? jumpRange(
          ladenMass,
          fuelLoad,
          maxFuelPerJump,
          optimalMass,
          fuelMultiplier,
          fuelPower,
        )
      : null;

  const currentMass = ladenMass + fuelLoad;
  const maxMass = unladenMass + cargoCapacity + fuelCapacity;
  const thrusterMass = currentMass;
  const minMass = stat(thrusters, "minmass");
  const thrusterOptMass = stat(thrusters, "optmass");
  const thrusterMaxMass = stat(thrusters, "maxmass");
  const minMultiplier = stat(thrusters, "minmul");
  const optMultiplier = stat(thrusters, "optmul");
  const maxMultiplier = stat(thrusters, "maxmul");
  const thrustMultiplier =
    thrusters &&
    minMass !== null &&
    thrusterOptMass !== null &&
    thrusterMaxMass !== null &&
    minMultiplier !== null &&
    optMultiplier !== null &&
    maxMultiplier !== null
      ? massCurveMultiplier(
          thrusterMass,
          minMass,
          thrusterOptMass,
          thrusterMaxMass,
          minMultiplier,
          optMultiplier,
          maxMultiplier,
        )
      : null;
  const speed = ship.speed !== null && thrustMultiplier !== null ? ship.speed * thrustMultiplier : null;
  const boostEnergy = ship.boostEnergy;
  const engCap = stat(powerDistributor, "engcap");
  const boost =
    ship.boost !== null &&
    thrustMultiplier !== null &&
    boostEnergy !== null &&
    engCap !== null &&
    engCap >= boostEnergy
      ? ship.boost * thrustMultiplier
      : null;

  // Shield generators compare against the bare hull, not armour or outfitting mass.
  const hullMass = ship.hullMass;
  const shieldGeneratorMaxMass = stat(shieldGenerator, "maxmass");
  const shieldMinMass = stat(shieldGenerator, "minmass");
  const shieldOptMass = stat(shieldGenerator, "optmass");
  const shieldMinMultiplier = stat(shieldGenerator, "minmul");
  const shieldOptMultiplier = stat(shieldGenerator, "optmul");
  const shieldMaxMultiplier = stat(shieldGenerator, "maxmul");
  const shieldBoost =
    1 +
    modules
      .filter((module) => module.group === "sb")
      .reduce((total, module) => total + (stat(module, "shieldboost") ?? 0), 0);
  const shieldMultiplier =
    shieldGenerator &&
    hullMass !== null &&
    shieldGeneratorMaxMass !== null &&
    shieldGeneratorMaxMass >= hullMass &&
    shieldMinMass !== null &&
    shieldOptMass !== null &&
    shieldMinMultiplier !== null &&
    shieldOptMultiplier !== null &&
    shieldMaxMultiplier !== null
      ? massCurveMultiplier(
          hullMass,
          shieldMinMass,
          shieldOptMass,
          shieldGeneratorMaxMass,
          shieldMinMultiplier,
          shieldOptMultiplier,
          shieldMaxMultiplier,
        )
      : null;
  const shield =
    ship.baseShieldStrength !== null && shieldMultiplier !== null
      ? ship.baseShieldStrength * shieldMultiplier * shieldBoost
      : null;

  const armourMultiplier = 1 + (bulkhead?.hullBoost ?? 0);
  const armour = ship.baseArmour !== null
    ? ship.baseArmour * armourMultiplier + modules.reduce((total, module) => total + (stat(module, "hullreinforcement") ?? 0), 0)
    : null;

  // EDSY combines reinforcement resistances multiplicatively, applying its
  // resistance soft cap to the combined bulkhead and reinforcement values.
  const armourResistance = (base: number | null | undefined, key: string): number | null => {
    if (base === null || base === undefined) return null;
    const resistances = modules.filter((module) => ["hr", "mahr", "ghrp"].includes(module.group ?? ""))
      .map((module) => stat(module, key) ?? 0);
    const multiplier = resistances.reduce((value, resistance) => value * (1 - resistance), 1);
    const best = Math.max(0, ...resistances);
    const low = Math.max(0.3, base, best);
    const expected = 1 - (1 - base) * multiplier;
    const penalized = low + ((expected - low) / (1 - low)) * (0.65 - low);
    return penalized >= 0.3 ? penalized : expected;
  };

  const powerCapacity = stat(powerPlant, "pgen");
  const powerDraw = modules.reduce(
    (total, module) => total + modulePower(module),
    0,
  );
  const powerRetracted = fits.reduce((total, fit) => {
    if (!fit.module || fit.slot.kind === "hardpoint") return total;
    const effective = fit.engineering
      ? applyEngineeringModifiers(fit.module, fit.engineering, engineeringRoll).module
      : fit.module;
    return total + modulePower(effective);
  }, 0);

  const price =
    (ship.retailCost ?? 0) +
    (bulkhead?.cost ?? 0) +
    modules.reduce(
      (total, module) =>
        total + (module.purchaseCurrency === "merc-coin" ? 0 : (module.cost ?? 0)),
      0,
    );

  const warnings: string[] = [...engineeringWarnings];
  if (!powerPlant) warnings.push("No power plant fitted.");
  if (!fsd) warnings.push("No frame shift drive fitted.");
  if (!thrusters) warnings.push("No thrusters fitted.");
  if (fuelCapacity <= 0) warnings.push("No fuel tank fitted.");
  if (powerCapacity !== null && powerDraw > powerCapacity) {
    warnings.push("Power draw exceeds power plant output.");
  }
  if (shieldGenerator && shield === null) {
    warnings.push(hullMass !== null && shieldGeneratorMaxMass !== null && hullMass > shieldGeneratorMaxMass
      ? "Shield generator cannot cover the current hull mass."
      : "Shield strength cannot be calculated from the available data.");
  }
  if (thrusters && thrusterMaxMass !== null && thrusterMass > thrusterMaxMass) {
    warnings.push("Thrusters are overloaded for the current mass.");
  }
  if (boostEnergy !== null && engCap !== null && engCap < boostEnergy) {
    warnings.push("Power distributor cannot sustain boost.");
  }
  if (!bulkhead) warnings.push("No bulkhead fitted; showing base armour.");

  return {
    moduleCount: modules.length,
    price,
    unladenMass,
    ladenMass,
    currentMass,
    maxMass,
    cargoCapacity,
    cargoLoad,
    fuelCapacity,
    fuelLoad,
    fuelReserve: ship.reserveFuelCapacity ?? 0,
    jumpMax,
    jumpUnladen,
    jumpLaden,
    rangeUnladen,
    rangeLaden,
    speed,
    boost,
    shield,
    armour,
    armourKineticResistance: armourResistance(bulkhead?.kineticResistance, "kinres"),
    armourThermalResistance: armourResistance(bulkhead?.thermalResistance, "thermres"),
    armourExplosiveResistance: armourResistance(bulkhead?.explosiveResistance, "explres"),
    armourCausticResistance: armourResistance(bulkhead?.causticResistance, "causres"),
    powerCapacity,
    powerDraw,
    powerRetracted,
    warnings,
  };
}
